// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import {
  Component,
  ChangeDetectionStrategy,
  computed,
  input,
  output,
  ViewChild,
  TemplateRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Column } from '@lfx/shared/interfaces';
import { NewTableComponent } from '@lfx/shared/components/new-table/new-table.component';
import { SharedModule } from '@lfx/shared/shared.module';
import {
  TableDataRow,
  ViewState,
  EmptyStateConfig,
  Workspace,
  KeyProject,
} from '@lfx/key-projects/key-projects.model';
import { AvatarWithNameColumnComponent } from '../avatar-with-name-column/avatar-with-name-column.component';
import { HealthScoreColumnComponent } from './health-score-column/health-score-column.component';
import { InfluenceColumnComponent } from './influence-column/influence-column.component';
import { ContributorsColumnComponent } from './contributors-column/contributors-column.component';
import { ParticipantsColumnComponent } from './participants-column/participants-column.component';
import { ActivityColumnComponent } from './activity-column/activity-column.component';
import { ButtonComponent } from '@lfx/shared/components/button';
import { EmptyStateComponent } from '@lfx/key-projects/components/empty-state/empty-state.component';
import { WorkspaceDropdownComponent } from '@lfx/key-projects/components/workspace-dropdown/workspace-dropdown.component';
import { WorkspaceSettingsComponent } from '@lfx/key-projects/components/workspace-settings/workspace-settings.component';
import {
  DropdownMenuComponent,
  DropdownMenuItem,
  DropdownMenuClickEvent,
} from '@lfx/shared/components/dropdown-menu';
import { KEY_PROJECTS_TEXT } from '@lfx/key-projects/key-projects.helper';
import { LEVEL_STYLE_MAPPING } from '@lfx/key-projects/entities/base-metric.entity';
import { TableDataTransformerService } from '@lfx/key-projects/services/table-data-transformer.service';
import { TableConfigurationService } from '@lfx/key-projects/services/table-configuration.service';
import { TableSortingService } from '@lfx/key-projects/services/table-sorting.service';

@Component({
  selector: 'lfx-key-projects-table',
  imports: [
    CommonModule,
    RouterModule,
    NewTableComponent,
    SharedModule,
    AvatarWithNameColumnComponent,
    HealthScoreColumnComponent,
    InfluenceColumnComponent,
    ContributorsColumnComponent,
    ParticipantsColumnComponent,
    ActivityColumnComponent,
    ButtonComponent,
    EmptyStateComponent,
    WorkspaceDropdownComponent,
    WorkspaceSettingsComponent,
    DropdownMenuComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <lfx-lf-box
      [isLoading]="isLoading()"
      [version]="2"
      [spinnerName]="'key-projects-table'"
      class="key-projects-box"
    >
      <div class="key-projects-table">
        <!-- Table Header Controls -->
        <div class="table-controls">
          <div class="filter-section">
            <!-- Workspace Dropdown (Always Visible) -->
            <lfx-workspace-dropdown
              [workspaces]="workspaces()"
              [viewState]="viewState()"
              (workspaceSelected)="onWorkspaceClick($event)"
              (addWorkspaceClicked)="onAddWorkspaceClicked()"
              (rebuildAllWorkspacesClicked)="onRebuildAllWorkspacesClicked()"
            >
            </lfx-workspace-dropdown>
          </div>

          <div class="export-section">
            @if (!shouldShowEmptyState()) {
              <lfx-button
                icon="far fa-download"
                variant="neutral"
                size="large"
                svgIcon="assets/img/svgs/csv.svg"
                (click)="onExportClick()"
              >
                Export to CSV
              </lfx-button>
            }

            <!-- Add Project Button -->
            @if (!shouldShowEmptyState()) {
              <lfx-button
                icon="far fa-plus"
                variant="secondary"
                size="large"
                (click)="onAddProjectClick()"
              >
                Add project
              </lfx-button>
            }

            <!-- Workspace Settings - Only show in workspace mode -->
            @if (isWorkspaceMode()) {
              <lfx-workspace-settings
                [workspaceId]="getCurrentWorkspaceId()"
                [workspaceName]="getCurrentWorkspaceName()"
                (renameWorkspace)="onRenameWorkspace()"
                (deleteWorkspace)="onDeleteWorkspace($event)"
              >
              </lfx-workspace-settings>
            }
          </div>
        </div>

        <!-- Projects Table using lfx-new-table -->
        <div class="table-container">
          @if (!shouldShowEmptyState()) {
            <lfx-new-table
              [columns]="columns"
              [data]="sortedTableData()"
              [version]="2"
              [freezeHeader]="true"
              [rowsMaxHeight]="'none'"
              [emptyDataMessage]="'No data found'"
              [activeSortColumn]="activeSortColumn()"
              (columnSortEmmitter)="handleColumnSort($event)"
              class="mb-4"
            >
            </lfx-new-table>
          }

          <!-- Workspace Projects Empty State -->
          @if (shouldShowEmptyState()) {
            <lfx-empty-state
              [config]="emptyStateConfig()!"
              (actionClicked)="onEmptyStateAction()"
            >
            </lfx-empty-state>
          }
        </div>
      </div>
    </lfx-lf-box>

    <!-- Column Templates -->
    <ng-template #projectColumn let-row="row">
      <lfx-avatar-with-name-column
        [info]="{
          slug: row.projectInfo.slug,
          logoUrl: row.projectInfo.logoUrl,
          name: row.projectInfo.name
        }"
      >
      </lfx-avatar-with-name-column>
    </ng-template>

    <ng-template #healthScoreColumn let-row="row">
      <lfx-health-score-column
        [variant]="row.healthScore"
        [projectSlug]="row.projectInfo.slug || row.id"
        [description]="row.projectInfo.description"
        [healthScoreRawData]="row.healthScoreRawData"
      >
      </lfx-health-score-column>
    </ng-template>

    <ng-template #technicalInfluenceColumn let-row="row">
      <a class="clickable-cell" [routerLink]="[row.projectInfo.slug]">
        <lfx-influence-column [displayText]="row.technicalInfluence" [variant]="LEVEL_STYLE_MAPPING[row.technicalInfluence]?.colorName">
        </lfx-influence-column>
      </a>
    </ng-template>

    <ng-template #ecosystemInfluenceColumn let-row="row">
      <a class="clickable-cell" [routerLink]="[row.projectInfo.slug]">
        <lfx-influence-column [displayText]="row.ecosystemInfluence" [variant]="LEVEL_STYLE_MAPPING[row.ecosystemInfluence]?.colorName">
        </lfx-influence-column>
      </a>
    </ng-template>

    <ng-template #contributorsColumn let-row="row">
      <a class="clickable-cell" [routerLink]="[row.projectInfo.slug]">
        <lfx-contributors-column
          [contributors]="row.contributors"
        ></lfx-contributors-column>
      </a>
    </ng-template>

    <ng-template #participantsColumn let-row="row">
      <a class="clickable-cell" [routerLink]="[row.projectInfo.slug]">
        <lfx-participants-column
          [participants]="row.participants"
        ></lfx-participants-column>
      </a>
    </ng-template>

    <ng-template #activityColumn let-row="row">
      <a class="clickable-cell" [routerLink]="[row.projectInfo.slug]">
        <lfx-activity-column [activity]="row.activity"></lfx-activity-column>
      </a>
    </ng-template>

    <ng-template #actionsColumn let-row="row">
      <lfx-dropdown-menu
        triggerIcon="fal fa-ellipsis-v"
        triggerAriaLabel="Project actions"
        triggerSize="large"
        [items]="getActionMenuItems()"
        (itemClicked)="onActionMenuClick($event, row)"
      >
      </lfx-dropdown-menu>
    </ng-template>
  `,
  styleUrls: ['./key-projects-table.component.scss'],
})
export class KeyProjectsTableComponent implements OnInit, OnChanges {
  // Inject services using modern Angular inject() function
  private readonly tableDataTransformer = inject(TableDataTransformerService);
  private readonly tableConfiguration = inject(TableConfigurationService);
  private readonly tableSorting = inject(TableSortingService);

  // Text constants
  protected readonly TEXT = KEY_PROJECTS_TEXT;
  protected readonly LEVEL_STYLE_MAPPING = LEVEL_STYLE_MAPPING;

  // Modern input/output syntax with signals
  readonly keyProjects = input<KeyProject[]>([]);
  readonly isLoading = input<boolean>(false);
  readonly viewState = input<ViewState | null>(null);
  readonly emptyStateConfig = input<EmptyStateConfig | null>(null);
  readonly workspaces = input<Workspace[]>([]);

  readonly exportRequested = output<void>();
  readonly workspaceSelected = output<string>();
  readonly emptyStateActionClicked = output<void>();
  readonly addWorkspaceClicked = output<void>();
  readonly removeProjectFromWorkspaceClicked = output<{
    projectId: string;
    projectName: string;
    workspaceId: string;
  }>();
  readonly renameWorkspaceClicked = output<void>();
  readonly deleteWorkspaceClicked = output<{ id: string; name: string }>();
  readonly addProjectClicked = output<void>();
  readonly rebuildAllWorkspacesClicked = output<void>();

  // Template references for lfx-new-table
  @ViewChild('projectColumn', { static: true })
  readonly projectColumn!: TemplateRef<any>;
  @ViewChild('healthScoreColumn', { static: true })
  readonly healthScoreColumn!: TemplateRef<any>;
  @ViewChild('technicalInfluenceColumn', { static: true })
  readonly technicalInfluenceColumn!: TemplateRef<any>;
  @ViewChild('ecosystemInfluenceColumn', { static: true })
  readonly ecosystemInfluenceColumn!: TemplateRef<any>;
  @ViewChild('contributorsColumn', { static: true })
  readonly contributorsColumn!: TemplateRef<any>;
  @ViewChild('participantsColumn', { static: true })
  readonly participantsColumn!: TemplateRef<any>;
  @ViewChild('activityColumn', { static: true })
  readonly activityColumn!: TemplateRef<any>;
  @ViewChild('actionsColumn', { static: true })
  readonly actionsColumn!: TemplateRef<any>;

  // Table columns configuration
  columns: Column[] = [];

  // Sorting state - uses centralized default configuration
  protected readonly activeSortColumn = signal<string>(
    TableConfigurationService.DEFAULT_SORT_COLUMN
  );
  protected readonly activeSortProp = signal<string>(
    TableConfigurationService.DEFAULT_SORT_COLUMN
  );
  protected readonly sortDirection = signal<'asc' | 'desc'>(
    TableConfigurationService.DEFAULT_SORT_DIRECTION
  );

  ngOnInit(): void {
    this.setColumns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update columns when view state changes
    if (changes['viewState']) {
      this.setColumns();
    }
  }

  /**
   * Set table columns using configuration service
   */
  private setColumns(): void {
    this.columns = this.tableConfiguration.getColumns(this.viewState(), {
      projectColumn: this.projectColumn,
      healthScoreColumn: this.healthScoreColumn,
      technicalInfluenceColumn: this.technicalInfluenceColumn,
      ecosystemInfluenceColumn: this.ecosystemInfluenceColumn,
      contributorsColumn: this.contributorsColumn,
      participantsColumn: this.participantsColumn,
      activityColumn: this.activityColumn,
      actionsColumn: this.actionsColumn,
    });
  }

  /**
   * Computed table data - delegates transformation to service
   */
  protected readonly tableData = computed((): TableDataRow[] => {
    return this.tableDataTransformer.transformToTableData(this.keyProjects());
  });

  /**
   * Computed sorted table data
   */
  protected readonly sortedTableData = computed((): TableDataRow[] => {
    const data = this.tableData();
    const sortProp = this.activeSortProp();
    const sortDir = this.sortDirection();

    return this.tableSorting.sortTableData(data, sortProp, sortDir);
  });

  protected onExportClick(): void {
    this.exportRequested.emit();
  }

  protected onWorkspaceClick(workspaceId: string): void {
    // Emit workspace selection from dropdown
    this.workspaceSelected.emit(workspaceId);
  }

  protected onEmptyStateAction(): void {
    this.emptyStateActionClicked.emit();
  }

  protected onAddWorkspaceClicked(): void {
    this.addWorkspaceClicked.emit();
  }

  protected onAddProjectClick(): void {
    this.addProjectClicked.emit();
  }

  protected onRebuildAllWorkspacesClicked(): void {
    this.rebuildAllWorkspacesClicked.emit();
  }

  /**
   * Check if current view is workspace mode
   */
  protected isWorkspaceMode(): boolean {
    return this.tableConfiguration.isWorkspaceMode(this.viewState());
  }

  /**
   * Get current workspace ID from view state
   */
  protected getCurrentWorkspaceId(): string {
    const state = this.viewState();
    return state?.mode === 'workspace-projects'
      ? state.selectedWorkspaceId || ''
      : '';
  }

  /**
   * Get current workspace name
   */
  protected getCurrentWorkspaceName(): string {
    const state = this.viewState();
    if (state?.mode === 'workspace-projects' && state.selectedWorkspaceId) {
      const currentWorkspace = this.workspaces().find(ws => ws.id === state.selectedWorkspaceId);
      return currentWorkspace?.name || '';
    }
    return '';
  }

  /**
   * Handle workspace rename event
   */
  protected onRenameWorkspace(): void {
    this.renameWorkspaceClicked.emit();
  }

  /**
   * Handle workspace delete event
   */
  protected onDeleteWorkspace(event: { id: string; name: string }): void {
    this.deleteWorkspaceClicked.emit(event);
  }

  /**
   * Get action menu items for project actions
   * Delegates to configuration service
   */
  protected getActionMenuItems(): DropdownMenuItem[] {
    return this.tableConfiguration.getActionMenuItems();
  }

  /**
   * Handle action menu click events
   */
  protected onActionMenuClick(
    event: DropdownMenuClickEvent,
    row: TableDataRow
  ): void {
    switch (event.action) {
      case 'remove-project':
        this.removeProjectFromWorkspaceClicked.emit({
          projectId: row.id,
          projectName: row.projectInfo?.name || 'Unknown project',
          workspaceId: this.getCurrentWorkspaceId(),
        });
        break;
      default:
        console.warn('Unknown project action:', event.action);
    }
  }

  /**
   * Determine if empty state should be shown
   */
  protected shouldShowEmptyState(): boolean {
    return this.emptyStateConfig() !== null && this.keyProjects().length === 0;
  }

  /**
   * Handle column sort event
   */
  protected handleColumnSort(column: Column): void {
    if (!column.sortable) {
      return;
    }

    // Update the active sort column and direction signals
    this.activeSortColumn.set(column.prop); // For UI highlighting
    this.activeSortProp.set(column.sortProp || column.prop); // For actual sorting
    this.sortDirection.set(column.sortDir);

    // Update the column sort direction in the columns array for UI state
    this.columns = this.columns.map(col =>
      col.prop === column.prop ? { ...col, sortDir: column.sortDir } : col
    );
  }

}
